import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ASSISTANT_ERROR_CODES,
  CHAT_HOUSEHOLD_MERGED,
  type AssistantFeedbackList,
  type AssistantFeedbackQuery,
  type AssistantStatus,
  type ChatFeedbackInput,
  type ChatPromptTestResult,
  type ChatTestTurn,
  type ChatView,
} from '@hv/shared';
import { ApiError, del, get, post, qs } from '../api/client';
import { useAuth } from '../components/AuthContext';

export const assistantKeys = {
  all: ['assistant'] as const,
  status: ['assistant', 'status'] as const,
  chat: (householdId: string) => ['assistant', 'chat', householdId] as const,
  ask: ['assistant', 'ask'] as const,
  feedback: (q: Partial<AssistantFeedbackQuery>) => ['assistant', 'feedback', q] as const,
  retry: ['assistant', 'retry'] as const,
};

export interface AskChatVars {
  householdId: string;
  text: string;
}

export function useAssistantStatus() {
  const { can } = useAuth();
  return useQuery({ queryKey: assistantKeys.status, queryFn: () => get<AssistantStatus>('/assistant'), enabled: can('households.chat'), staleTime: 60_000 });
}

export function useAssistantAvailable(): boolean {
  const { can } = useAuth();
  const status = useAssistantStatus();
  return can('households.chat') && status.data?.enabled === true;
}

export const useChat = (householdId: string) =>
  useQuery({ queryKey: assistantKeys.chat(householdId), queryFn: () => get<ChatView>(`/households/${householdId}/chat`) });

function useSetChat() {
  const qc = useQueryClient();
  return (view: ChatView) => qc.setQueryData(assistantKeys.chat(view.householdId), view);
}

function useReloadIfMerged() {
  const qc = useQueryClient();
  return (err: unknown, householdId: string) => {
    if (err instanceof ApiError && err.code === CHAT_HOUSEHOLD_MERGED) void qc.invalidateQueries({ queryKey: assistantKeys.chat(householdId) });
  };
}

const LIMIT_CODES: readonly string[] = [ASSISTANT_ERROR_CODES.turnLimit, ASSISTANT_ERROR_CODES.rateLimited];

export const isLimitError = (err: unknown): boolean => err instanceof ApiError && LIMIT_CODES.includes(err.code ?? '');

export function useAskChat() {
  const qc = useQueryClient();
  const setChat = useSetChat();
  const reloadIfMerged = useReloadIfMerged();
  return useMutation({
    mutationKey: assistantKeys.ask,
    mutationFn: (v: AskChatVars) => post<ChatView>(`/households/${v.householdId}/chat/messages`, { text: v.text }),
    onSuccess: setChat,
    onError: (err, v) => {
      console.error('Asking the assistant failed', { householdId: v.householdId, err });
      reloadIfMerged(err, v.householdId);
      if (isLimitError(err)) void qc.invalidateQueries({ queryKey: assistantKeys.chat(v.householdId) });
    },
  });
}

export function usePendingQuestion(householdId: string): string | null {
  const pending = useMutationState({
    filters: { mutationKey: assistantKeys.ask, status: 'pending' },
    select: (m) => m.state.variables as AskChatVars | undefined,
  });
  return pending.find((v) => v?.householdId === householdId)?.text ?? null;
}

export function useNewChat() {
  const setChat = useSetChat();
  const reloadIfMerged = useReloadIfMerged();
  return useMutation({
    mutationFn: (householdId: string) => post<ChatView>(`/households/${householdId}/chat/new`),
    onSuccess: setChat,
    onError: (err, householdId) => reloadIfMerged(err, householdId),
  });
}

export function useDeleteChat() {
  const setChat = useSetChat();
  return useMutation({
    mutationFn: (threadId: string) => del<ChatView>(`/chat/threads/${threadId}`),
    onSuccess: setChat,
  });
}

export function useRateMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { messageId: string } & ChatFeedbackInput) =>
      post<ChatView>(`/chat/messages/${v.messageId}/feedback`, { rating: v.rating, ...(v.comment ? { comment: v.comment } : {}) }),
    onSuccess: (view) => {
      qc.setQueryData(assistantKeys.chat(view.householdId), view);
      void qc.invalidateQueries({ queryKey: ['assistant', 'feedback'] });
    },
  });
}

export const useAssistantFeedback = (q: Partial<AssistantFeedbackQuery>) =>
  useQuery({
    queryKey: assistantKeys.feedback(q),
    queryFn: () => get<AssistantFeedbackList>(`/admin/assistant-feedback${qs({ promptVersion: q.promptVersion, rating: q.rating, from: q.from, to: q.to, limit: q.limit })}`),
  });

export interface RetryChatVars {
  householdId: string;
  messageId: string;
}

export function useRetryChat() {
  const qc = useQueryClient();
  const setChat = useSetChat();
  return useMutation({
    mutationKey: assistantKeys.retry,
    mutationFn: (v: RetryChatVars) => post<ChatView>(`/chat/messages/${v.messageId}/retry`),
    onSuccess: setChat,
    onError: (err, v) => {
      console.error('Retrying the assistant answer failed', { householdId: v.householdId, messageId: v.messageId, err });
      if (isLimitError(err)) void qc.invalidateQueries({ queryKey: assistantKeys.chat(v.householdId) });
    },
  });
}

export function usePendingRetry(householdId: string): string | null {
  const pending = useMutationState({
    filters: { mutationKey: assistantKeys.retry, status: 'pending' },
    select: (m) => m.state.variables as RetryChatVars | undefined,
  });
  return pending.find((v) => v?.householdId === householdId)?.messageId ?? null;
}

export interface TestChatPromptVars {
  version: string;
  householdId: string;
  history: ChatTestTurn[];
  question: string;
}

export function useTestChatPrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: TestChatPromptVars) =>
      post<ChatPromptTestResult>(`/settings/assistant/prompt-versions/${encodeURIComponent(v.version)}/test`, { householdId: v.householdId, history: v.history, question: v.question }),
    onError: (err, v) => console.error('Testing the chat prompt failed', { version: v.version, householdId: v.householdId, err }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['assistant', 'chat'] }),
  });
}
